<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import GenerationProgress from "./partials/GenerationProgress.vue";
import BaseAlert from "../global/alerts/BaseAlert.vue";
import GeneratedContent from "./partials/GeneratedContent.vue";

const store = useStore();
const website = computed(() => store.getters['user/user']?.active_website);
const plan = computed(() => store.getters['blogGeneration/plan']);
const generating = computed(() => store.getters['blogGeneration/generating']);
const processing = computed(() => store.getters['blogGeneration/startingGeneration']);
const generatingFailed = computed(() => store.getters['blogGeneration/generatingFailed']);
const progress = ref(0);
const progressInterval = ref(undefined);

onMounted(async () => {
  await store.dispatch('user/getUser');
  await store.dispatch('blogGeneration/getPlan');
  if (generating.value === true) {
    if (progressInterval.value) {
      clearInterval(progressInterval.value);
    }
    startFakeLoadingBar();
    store.dispatch('blogGeneration/pollGeneration');
  }
})

const daysUntilRefresh = computed(() => {
  const dateToday = new Date();
  const lastDayOfMonth = new Date(dateToday.getFullYear(), dateToday.getMonth() + 1, 0);
  return lastDayOfMonth.getDate() - dateToday.getDate();
})

const generate = async () => {
  await store.dispatch('blogGeneration/startGenerating');
  store.dispatch('user/getUser'); // update credits count.
  if (progressInterval.value) {
    clearInterval(progressInterval.value);
  }
  startFakeLoadingBar();
}

const startFakeLoadingBar = () => {
  progress.value = 0;
  let current_progress = 0;
  let step = 0.2;
  progressInterval.value = setInterval(() => {
    current_progress += step;
    progress.value = Math.round(Math.atan(current_progress) / (Math.PI / 2) * 100 * 1000) / 1000
    if (progress.value >= 100) {
      clearInterval(progressInterval.value);
    } else if(progress.value >= 70) {
      step = 0.1
    }
  }, 350);
}
</script>

<template>
  <div class="blog-generation">
    <div v-if="plan">
      <div
        v-if="!plan?.gpt3content"
        class="xs-mb-10"
      >
        <div class="d-flex">
          <h1 class="xs-mb-5 flex-fill">
            Create a blog with the Content Wizard 🧙‍♂️
          </h1>
        </div>

        <div class="font-size-16 mb-4">
          <p class="xs-mb-10">
            Let the Content Wizard write up to 700 words of unique copy that you can use as the structure for your blog post. Don't forget to make the blog unique by adding your own ideas, examples and tone of voice.
          </p>
          <p>
            It will only take him a few minutes!
          </p>
        </div>
      </div>
      <GeneratedContent
        v-else-if="!generating"
        :title="plan.content_title"
        :content="plan.gpt3content"
      />

      <BaseAlert
        v-if="generatingFailed === true"
        type="danger"
      >
        The Content Wizard is encountering some issues right now, please try again in a few minutes. If the problem persists please reach out to support.
      </BaseAlert>

      <GenerationProgress
        v-if="generating && !generatingFailed"
        :progress="progress"
      />
      <div
        v-else-if="website.gpt3_credits > 0"
        class="text-center xs-mt-20"
      >
        <button
          :disabled="processing"
          class="btn btn-lg xs-mt-20 xs-mb-20"
          :class="plan.gpt3content || generatingFailed ? 'btn-outline-secondary' : 'btn-success'"
          @click="generate"
        >
          <i
            v-if="processing || plan.gpt3content || generatingFailed"
            :class="{'fa fa-pulse fa-spinner': processing, 'far fa-sync':!processing}"
          />
          {{ plan.gpt3content || generatingFailed ? 'Re-Run Content Wizard' : 'Run Content Wizard' }}
        </button>
      </div>
      <div v-else>
        <div class="text-center xs-mt-20">
          <p>
            You have used all your Content Wizard fair-usage allowance this month. This will refresh in {{ daysUntilRefresh }} days.
          </p>
        </div>
      </div>
    </div>
    <div
      v-else
      class="xs-p-20 text-center"
    >
      <i class="fa fa-pulse fa-spinner font-size-45" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.blog-generation {
  width: 750px;
}
</style>
